import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem("uneptoken") || "");
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("unepuser");
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      localStorage.setItem("uneptoken", token);
    } else {
      localStorage.removeItem("uneptoken");
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("unepuser", JSON.stringify(user));
    } else {
      localStorage.removeItem("unepuser");
    }
  }, [user]);

  useEffect(() => {
    function handleAuthExpired() {
      setToken("");
      setUser(null);
      toast.info("Your session expired. Please sign in again.");
    }

    window.addEventListener("auth:expired", handleAuthExpired);

    return () => {
      window.removeEventListener("auth:expired", handleAuthExpired);
    };
  }, []);

  async function login(values) {
    setLoading(true);

    try {
      const response = await api.post("/users/login", values);
      setToken(response.data.accessToken);
      setUser(response.data.user);
      toast.success("Welcome back");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.error || "Login failed");
      return false;
    } finally {
      setLoading(false);
    }
  }

  function logout() {
    setToken("");
    setUser(null);
    toast.info("Signed out");
  }

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      login,
      logout,
      isAuthenticated: Boolean(token)
    }),
    [token, user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
